<template>
  <div class="exact-modal-step exact-modal-step--detail">
    <div class="step-header">
      <p class="step-header-title">{{ $t('registration.detailTarget.ad.title') }}</p>
      <ba-switch :value="isToggleOn" :default="false" @change="$emit('update:is-toggle-on', !isToggleOn)" />
    </div>
    <p class="step-desc">
      <template v-if="isToggleOn">{{ $t('registration.detailTarget.ad.descriptionOn') }}</template>
      <template v-else>{{ $t('registration.detailTarget.ad.descriptionOff') }}</template>
    </p>

    <div class="example">
      <p class="example-title">{{ $t('registration.detailTarget.ad.info') }}</p>
      <div class="example-content">
        <div class="example-content-item item-1" v-if="!isToggleOn">
          <p>{{ $t('registration.detailTarget.ad.example.baseUrl') }}</p>
          <i class="icon icon--ba-arrow"></i>
        </div>
        <div class="example-content-item item-2">
          <p>{{ $t('registration.detailTarget.ad.example.googleUrl') }}</p>
          <p>{{ $t('registration.detailTarget.ad.example.googlePv') }}</p>
        </div>
        <div class="example-content-item item-3">
          <p>{{ $t('registration.detailTarget.ad.example.naverUrl') }}</p>
          <p>{{ $t('registration.detailTarget.ad.example.naverPv') }}</p>
        </div>
        <div class="example-content-item-wrap" v-if="isToggleOn">
          <i class="icon icon--ba-arrow size-20"></i>
          <div class="example-content-item item-4">
            <p>{{ $t('registration.detailTarget.ad.example.baseUrl') }}</p>
            <p>{{ $t('registration.detailTarget.ad.example.mergedPv') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="step-content" v-if="isToggleOn">
      <p class="step-content-title">{{ $t('registration.detailTarget.ad.autoRemoved') }}</p>

      <div class="step-content-tabs" role="tablist" aria-label="자동 제거 파라미터 카테고리">
        <button
          v-for="category in categories"
          :key="category.value"
          type="button"
          class="step-content-tab"
          :class="{ active: selectedCategory === category.value }"
          @click="selectedCategory = category.value"
        >
          <span class="label">{{ category.label }}</span>
          <span class="count">{{ category.count }}</span>
        </button>
      </div>

      <div class="step-content-tags">
        <div
          v-for="tag in selectedTags"
          :key="tag"
          class="step-content-tag"
        >
          <span class="tag-text">{{ tag }}</span>
        </div>
      </div>

      <div class="step-content-divider"></div>

      <div class="step-content-input-header">
        <p class="title">{{ $t('registration.detailTarget.ad.manualInput') }}</p>
        <p class="desc">{{ $t('registration.detailTarget.ad.manualInputDesc') }}</p>
      </div>

      <div class="step-content-input-wrap" :class="{ 'has-error': Boolean(inputErrorMessage) }">
        <ui-input-text
          :type="'text'"
          :isInputAbove="false"
          labelPosition="left"
          :modelValue="customParameterInput"
          :placeholder="$t('registration.detailTarget.ad.inputPlaceholder')"
          :error="inputErrorMessage"
          @input="onInputCustomParameter"
          @onEnter="onSubmitCustomParameter"
        />
      </div>

      <div v-if="customParameters.length" class="step-content-tags">
        <div
          v-for="tag in customParameters"
          :key="tag"
          class="step-content-tag"
        >
          <span class="tag-text">{{ tag }}</span>
          <button
            type="button"
            class="tag-remove"
            @click="removeCustomParameter(tag)"
          >
            <i class="icon icon--close-size24 size-8"></i>
          </button>
        </div>
      </div>
    </div>
    <div v-else></div>
  </div>
</template>

<script>
import { getAdParamConfig } from '@/api/domain'
import BaSwitch from '@/components/ui/ba/Switch'
import UiInputText from '@/components/ui/UiInputText'
import { getAdParamCategories, getAdParamTagMap, getAllDefaultAdParamKeys } from '@/libs/ad-param-config'

export default {
  name: 'SettingExactDetail',
  components: {
    BaSwitch,
    UiInputText
  },
  props: {
    isToggleOn: {
      type: Boolean,
      default: true
    },
    customParameters: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      defaultAdParams: [],
      selectedCategory: 'utm',
      customParameterInput: '',
      inputErrorMessage: ''
    }
  },
  mounted () {
    this.fetchAdParamConfig()
  },
  computed: {
    categories () {
      return getAdParamCategories(this.defaultAdParams)
    },
    selectedTags () {
      const tagMap = getAdParamTagMap(this.defaultAdParams)
      return tagMap[this.selectedCategory] || []
    }
  },
  methods: {
    fetchAdParamConfig () {
      getAdParamConfig({}, true)
        .then(result => {
          this.defaultAdParams = result.defaultAdParams || []
        })
        .catch(() => {
          this.defaultAdParams = []
        })
    },
    clearInputError () {
      this.inputErrorMessage = ''
    },
    onInputCustomParameter (value) {
      this.customParameterInput = value
      this.clearInputError()
    },
    clearCustomParameterInput () {
      this.customParameterInput = ''
      this.clearInputError()
    },
    removeCustomParameter (tag) {
      this.$emit('update:custom-parameters', this.customParameters.filter(item => item !== tag))
    },
    onSubmitCustomParameter () {
      const value = this.customParameterInput.trim()
      const defaultAdParams = getAllDefaultAdParamKeys(this.defaultAdParams)

      if (!value) {
        this.clearCustomParameterInput()
        return
      }

      if (!/^[A-Za-z0-9_.-]+$/.test(value)) {
        this.inputErrorMessage = this.$t('registration.detailTarget.ad.inputErrorInvalid')
        return
      }

      if (value.length > 30) {
        this.inputErrorMessage = this.$t('registration.detailTarget.ad.inputErrorMaxLength')
        return
      }

      if (defaultAdParams.includes(value) || this.customParameters.includes(value)) {
        this.inputErrorMessage = this.$t('registration.detailTarget.ad.inputErrorDuplicate')
        return
      }

      this.$emit('update:custom-parameters', [...this.customParameters, value])
      this.clearCustomParameterInput()
    }
  }
}
</script>

<style lang="scss" scoped>
.exact-modal-step {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 100%;
  margin-top: 5px;
  .step-header {
    display: flex;
    align-items: center;
    gap: 12px;
    &-title {
      color: #2f2f2f;
      font-size: 14px;
      font-weight: 500;
      line-height: 1;
    }
    :deep(.ba-switch) {
      button label {
        top: 1px;
      }
    }
  }
  .step-desc {
    color: #666;
    font-size: 14px;
    line-height: 1.2;
  }
  .example {
    position: relative;
    padding: 20px;
    border-radius: 5px;
    background-color: #2f2f2f;
    color: #fff;
    &-title {
      @include flexXYcenter;
      position: absolute;
      top: 8px;
      left: 8px;
      height: 20px;
      padding: 0 5px;
      background-color: #fff;
      color: #0e90a5;
      font-size: 12px;
      font-weight: 500;
      border-radius: 4px;
    }
    &-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 416px;
      margin: 0 auto;
      &-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 8px;
        border-radius: 5px;
        color: #262d50;
        font-size: 12px;
        font-weight: 500;
        line-height: 1;
        &-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 14px;
        }
        &.item-1 {
          justify-content: flex-start;
          gap: 12px;
          color: #fff;
        }
        &.item-2 {
          background-color: #d8deff;
        }
        &.item-3 {
          background-color: #bac5ff;
        }
        &.item-4 {
          flex: 1;
          height: 36px;
          background-color: #b6dc92;
          font-size: 14px;
        }
      }
    }
  }
  .step-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    padding: 20px;
    border: 1px solid #d7d7d7;
    border-radius: 5px;
    background: #f4f4f4;
    box-sizing: border-box;
    &-title {
      color: #2f2f2f;
      font-size: 14px;
      font-weight: 400;
      line-height: 1;
    }
    &-tabs {
      display: flex;
      align-items: stretch;
      width: fit-content;
      min-height: 36px;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      overflow: hidden;
      background: #fff;
    }
    &-tab {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      min-width: 64px;
      min-height: 36px;
      padding: 0 10px;
      border-right: 1px solid #e0e0e0;
      background: #fff;
      color: #555;
      font-size: 13px;
      font-weight: 400;
      line-height: 1;
      text-align: center;
      box-sizing: border-box;
      &:last-child {
        border-right: 0;
      }
      .count {
        color: #888;
      }
      &.active {
        background: #ec0047;
        color: #fff;
        .count {
          color: rgba(255, 255, 255, 0.76);
        }
        &:after {
          content: '';
          position: absolute;
          inset: 0;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.35);
          pointer-events: none;
        }
      }
    }
    &-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      width: 100%;
    }
    &-tag {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      height: 23px;
      padding: 4px 8px;
      border: 1px solid #ccc;
      border-radius: 999px;
      background: #fff;
      white-space: nowrap;
      box-sizing: border-box;
      .tag-text {
        font-weight: 400;
        font-size: 13px;
        line-height: 1;
        color: #777;
      }
      .tag-remove {
        @include flexXYcenter;
        width: 12px;
        height: 12px;
      }
    }
    &-divider {
      width: 100%;
      height: 1px;
      background: #ccc;
    }
    &-input-header {
      display: flex;
      align-items: flex-end;
      gap: 8px;
      line-height: 1;
      .title {
        color: #2f2f2f;
        font-size: 14px;
        font-weight: 600;
      }
      .desc {
        color: #777;
        font-size: 12px;
        font-weight: 400;
      }
    }
    &-input-wrap {
      width: 100%;
      &.has-error {
        margin-bottom: 16px;
      }
      & /deep/ {
        .input--wrap {
          .label {
            display: none;
          }
          .input-element {
            .validation-text {
              font-weight: 400;
            }
          }
        }
      }
    }
  }
}

</style>
